/**
 * IELTS Reading Exam — real-IELTS-style split-screen interface.
 *
 *   ┌─────────────────────────────┬─────────────────────────────┐
 *   │  PASSAGE (left)             │  QUESTIONS (right)          │
 *   │  - title                    │  - grouped by instructions  │
 *   │  - full text, no truncation │  - 16 question types        │
 *   │  - scrollable               │  - scrollable               │
 *   └─────────────────────────────┴─────────────────────────────┘
 *
 * Mobile: stacks vertically, passage first, questions below.
 *
 * Used by both `IeltsPracticePage` (untimed, with submission feedback) and
 * `IeltsMockTestPage` (timed, no inline feedback).
 */
import React, { useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Check, X } from 'lucide-react';
import { EXAM_TEXT_CLS } from '@/components/exam/QuestionDisplay';

// Maps for the canonical IELTS Reading question types.
const TFNG_OPTIONS = ['TRUE', 'FALSE', 'NOT GIVEN'];
const YNNG_OPTIONS = ['YES', 'NO', 'NOT GIVEN'];

const QTYPE_LABEL = {
  multiple_choice_one_answer: 'Multiple Choice',
  multiple_choice_multiple_answers: 'Multiple Choice (Multi)',
  true_false_not_given: 'True / False / Not Given',
  yes_no_not_given: 'Yes / No / Not Given',
  matching_headings: 'Matching Headings',
  matching_information: 'Matching Information',
  matching_features: 'Matching Features',
  matching_sentence_endings: 'Matching Sentence Endings',
  sentence_completion: 'Sentence Completion',
  summary_completion_from_text: 'Summary Completion (from text)',
  summary_completion_from_list: 'Summary Completion (from list)',
  note_completion: 'Note Completion',
  table_completion: 'Table Completion',
  flow_chart_completion: 'Flow-chart Completion',
  diagram_label_completion: 'Diagram-label Completion',
  short_answer: 'Short Answer',
};

const RADIO_LETTER_TYPES = new Set([
  'multiple_choice_one_answer',
  'matching_headings',
  'matching_information',
  'matching_features',
  'matching_sentence_endings',
]);
const TEXT_TYPES = new Set([
  'sentence_completion',
  'summary_completion_from_text',
  'summary_completion_from_list',
  'note_completion',
  'table_completion',
  'flow_chart_completion',
  'diagram_label_completion',
  'short_answer',
]);

/** Group consecutive questions by (question_type + instructions) so each block
 * shows real-IELTS-style "Questions 1–5 — Choose the correct letter…" headers. */
function groupQuestions(questions) {
  const groups = [];
  let cur = null;
  for (const q of questions) {
    const key = `${q.question_type}::${q.instructions || ''}`;
    if (!cur || cur.key !== key) {
      cur = {
        key,
        question_type: q.question_type,
        instructions: q.instructions || '',
        items: [],
      };
      groups.push(cur);
    }
    cur.items.push(q);
  }
  return groups;
}

const stateClasses = (selected, result, isCorrectAnswer, userPicked) => {
  if (result) {
    if (isCorrectAnswer) return 'border-green-500 bg-green-50';
    if (userPicked && !isCorrectAnswer) return 'border-red-500 bg-red-50';
    return 'border-gray-200 bg-white';
  }
  if (selected) return 'border-[#007A7A] bg-[#E6F4F4] shadow-sm';
  return 'border-gray-200 bg-white hover:border-gray-400 hover:bg-gray-50';
};

const RadioRow = ({ name, value, label, current, locked, result, correctValue, onPick, testId }) => {
  const isSelected = current === value;
  const isCorrect = result && correctValue && correctValue.toLowerCase() === value.toLowerCase();
  const userPicked = isSelected;
  return (
    <button
      type="button"
      onClick={locked ? undefined : () => onPick(value)}
      disabled={locked}
      data-testid={testId}
      className={`w-full text-left rounded-lg border-2 px-3 py-2 transition-all flex items-center gap-2.5 ${stateClasses(isSelected, result, isCorrect, userPicked)} ${locked ? 'cursor-default' : 'cursor-pointer'}`}
    >
      <span className={`flex-shrink-0 w-6 h-6 rounded-full border flex items-center justify-center text-[11px] font-bold uppercase ${
        result && isCorrect ? 'bg-green-500 border-green-500 text-white' :
        result && userPicked ? 'bg-red-500 border-red-500 text-white' :
        isSelected ? 'bg-[#007A7A] border-[#007A7A] text-white' :
        'bg-white border-gray-300 text-gray-700'
      }`}>
        {label}
      </span>
      <span className={`text-sm text-gray-900 ${EXAM_TEXT_CLS}`}>{value}</span>
      {result && isCorrect && <Check className="h-4 w-4 text-green-600 ml-auto" />}
      {result && userPicked && !isCorrect && <X className="h-4 w-4 text-red-600 ml-auto" />}
    </button>
  );
};

const McqRow = ({ letter, text, selected, locked, result, isCorrect, userPicked, onPick, testId }) => (
  <button
    type="button"
    onClick={locked ? undefined : () => onPick(letter)}
    disabled={locked}
    data-testid={testId}
    className={`w-full text-left rounded-lg border-2 px-3 py-2.5 transition-all flex items-start gap-3 ${stateClasses(selected, result, isCorrect, userPicked)} ${locked ? 'cursor-default' : 'cursor-pointer'}`}
  >
    <span className={`flex-shrink-0 w-6 h-6 mt-0.5 rounded-full border flex items-center justify-center text-[11px] font-bold uppercase ${
      result && isCorrect ? 'bg-green-500 border-green-500 text-white' :
      result && userPicked ? 'bg-red-500 border-red-500 text-white' :
      selected ? 'bg-[#007A7A] border-[#007A7A] text-white' :
      'bg-white border-gray-300 text-gray-700'
    }`}>
      {letter.toUpperCase()}
    </span>
    <span className={`flex-1 min-w-0 text-sm text-gray-900 ${EXAM_TEXT_CLS}`}>{text}</span>
    {result && isCorrect && <Check className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />}
    {result && userPicked && !isCorrect && <X className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />}
  </button>
);

/** A single question card inside a group. */
const QuestionItem = ({ q, value, locked, perResult, onChange }) => {
  const qt = q.question_type;
  const correct = perResult?.correct_answer;

  // ---- True/False/Not Given · Yes/No/Not Given (fixed 3-option radio)
  if (qt === 'true_false_not_given' || qt === 'yes_no_not_given') {
    const opts = qt === 'true_false_not_given' ? TFNG_OPTIONS : YNNG_OPTIONS;
    const letters = ['A', 'B', 'C'];
    return (
      <div className="space-y-1.5">
        {opts.map((opt, i) => (
          <RadioRow key={opt}
            name={q.id} value={opt} label={letters[i]}
            current={value} locked={locked} result={perResult} correctValue={correct}
            onPick={(v) => onChange(q.id, { text_answer: v })}
            testId={`ielts-rd-q${q.question_number}-opt-${letters[i]}`}
          />
        ))}
      </div>
    );
  }

  // ---- MCQ single answer + matching variants (letter answer)
  if (RADIO_LETTER_TYPES.has(qt)) {
    const opts = ['a', 'b', 'c', 'd', 'e']
      .map((l) => [l, q[`option_${l}`]])
      .filter(([, t]) => t && t.length > 0);
    return (
      <div className="space-y-1.5">
        {opts.map(([letter, text]) => {
          const isCorrect = !!perResult && correct && letter === correct.toLowerCase();
          const userPicked = value === letter;
          return (
            <McqRow key={letter}
              letter={letter}
              text={text}
              selected={!perResult && userPicked}
              locked={locked}
              result={perResult}
              isCorrect={isCorrect}
              userPicked={userPicked}
              onPick={(l) => onChange(q.id, { selected_option: l })}
              testId={`ielts-rd-q${q.question_number}-opt-${letter}`}
            />
          );
        })}
      </div>
    );
  }

  // ---- Multi-answer MCQ (checkboxes, comma-separated text_answer)
  if (qt === 'multiple_choice_multiple_answers') {
    const opts = ['a', 'b', 'c', 'd', 'e']
      .map((l) => [l, q[`option_${l}`]])
      .filter(([, t]) => t && t.length > 0);
    const picked = new Set((value || '').split(',').map((x) => x.trim().toLowerCase()).filter(Boolean));
    const correctSet = correct ? new Set(correct.split(',').map((x) => x.trim().toLowerCase()).filter(Boolean)) : null;
    return (
      <div className="space-y-1.5">
        {opts.map(([letter, text]) => {
          const isSelected = picked.has(letter);
          const isCorrect = correctSet?.has(letter);
          const userPicked = isSelected;
          const toggle = () => {
            const next = new Set(picked);
            if (next.has(letter)) next.delete(letter); else next.add(letter);
            onChange(q.id, { text_answer: Array.from(next).join(',') });
          };
          return (
            <button key={letter} type="button"
              onClick={locked ? undefined : toggle}
              disabled={locked}
              data-testid={`ielts-rd-q${q.question_number}-opt-${letter}`}
              className={`w-full text-left rounded-lg border-2 px-3 py-2.5 transition-all flex items-start gap-3 ${stateClasses(isSelected, perResult, isCorrect, userPicked)} ${locked ? 'cursor-default' : 'cursor-pointer'}`}
            >
              <span className={`flex-shrink-0 w-6 h-6 mt-0.5 rounded border flex items-center justify-center text-[11px] font-bold uppercase ${
                perResult && isCorrect ? 'bg-green-500 border-green-500 text-white' :
                perResult && userPicked ? 'bg-red-500 border-red-500 text-white' :
                isSelected ? 'bg-[#007A7A] border-[#007A7A] text-white' :
                'bg-white border-gray-300 text-gray-700'
              }`}>{letter.toUpperCase()}</span>
              <span className={`flex-1 min-w-0 text-sm text-gray-900 ${EXAM_TEXT_CLS}`}>{text}</span>
            </button>
          );
        })}
      </div>
    );
  }

  // ---- Free-text gap / completion / short-answer
  if (TEXT_TYPES.has(qt)) {
    const isCorrect = perResult?.is_correct;
    return (
      <div>
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(q.id, { text_answer: e.target.value })}
          disabled={locked}
          placeholder="Your answer…"
          data-testid={`ielts-rd-q${q.question_number}-input`}
          className={`w-full border-2 rounded-lg px-3 py-2 text-sm outline-none ${
            perResult
              ? (isCorrect ? 'border-green-400 bg-green-50' : 'border-red-400 bg-red-50')
              : 'border-gray-200 focus:border-red-700'
          }`}
        />
        {perResult && !isCorrect && correct && (
          <div className="text-xs mt-1 text-red-700">
            <span className="font-semibold">Expected:</span>{' '}
            <span className={EXAM_TEXT_CLS}>{correct}</span>
          </div>
        )}
      </div>
    );
  }

  // Default: text input fallback
  return (
    <input
      type="text"
      value={value || ''}
      onChange={(e) => onChange(q.id, { text_answer: e.target.value })}
      disabled={locked}
      placeholder="Your answer…"
      className="w-full border-2 rounded-lg px-3 py-2 text-sm outline-none border-gray-200 focus:border-red-700"
    />
  );
};

const IeltsReadingExam = ({
  passage,
  questions = [],
  answers = {},          // { questionId: { selected_option?, text_answer? } }
  results = null,        // { results: [{question_id, is_correct, correct_answer, explanation}], percent_correct, ... }
  onChangeAnswer,        // (qid, partial) => void
  locked = false,
  rightHeader = null,    // optional ReactNode rendered in the right column header
}) => {
  const groups = useMemo(() => groupQuestions(questions), [questions]);
  const perResultMap = useMemo(() => {
    const m = {};
    if (results?.results) {
      for (const r of results.results) m[r.question_id] = r;
    }
    return m;
  }, [results]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-5" data-testid="ielts-reading-exam">
      {/* LEFT — passage */}
      <Card className="border-gray-200" data-testid="ielts-reading-passage">
        <CardContent className="p-5 sm:p-6 lg:p-7">
          <div className="text-[11px] uppercase tracking-[0.2em] text-red-700 font-semibold mb-1.5">
            Reading Passage
          </div>
          <h2 className={`text-xl sm:text-2xl font-bold tracking-tight mb-3 ${EXAM_TEXT_CLS}`}>
            {passage?.passage_title}
          </h2>
          <div className="flex flex-wrap gap-1.5 mb-4">
            {passage?.passage_part && <Badge variant="outline" className="text-[10px] uppercase">{passage.passage_part.replace('_', ' ')}</Badge>}
            {passage?.module && <Badge variant="outline" className="text-[10px] uppercase">{passage.module}</Badge>}
            {passage?.topic && <Badge variant="outline" className="text-[10px] capitalize">{passage.topic}</Badge>}
            {passage?.difficulty && <Badge variant="outline" className="text-[10px] capitalize">{passage.difficulty}</Badge>}
          </div>
          <div
            className={`prose prose-sm max-w-none text-gray-900 lg:max-h-[72vh] lg:overflow-y-auto pr-1 ${EXAM_TEXT_CLS}`}
            style={{ fontFamily: 'Georgia, serif', lineHeight: 1.75 }}
            data-testid="ielts-reading-passage-text"
          >
            {(passage?.passage_text || '').split(/\n{2,}/).map((para, i) => (
              <p key={i} className="mb-4">{para}</p>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* RIGHT — questions */}
      <Card className="border-gray-200" data-testid="ielts-reading-questions">
        <CardContent className="p-5 sm:p-6 lg:p-7">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-red-700 font-semibold mb-1.5">
                Questions
              </div>
              <h3 className="text-xl sm:text-2xl font-bold tracking-tight">
                {questions.length} question{questions.length === 1 ? '' : 's'}
              </h3>
            </div>
            {rightHeader}
          </div>

          <div className="lg:max-h-[72vh] lg:overflow-y-auto pr-1 space-y-6">
            {groups.map((g, gi) => {
              const first = g.items[0]?.question_number;
              const last = g.items[g.items.length - 1]?.question_number;
              const range = first && last && first !== last ? `${first}–${last}` : `${first ?? ''}`;
              return (
                <div key={gi} data-testid={`ielts-rd-group-${gi}`}>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-3">
                    <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-1">
                      Questions {range} · {QTYPE_LABEL[g.question_type] || g.question_type.replace(/_/g, ' ')}
                    </div>
                    {g.instructions && (
                      <div className={`text-sm text-gray-800 ${EXAM_TEXT_CLS}`}>{g.instructions}</div>
                    )}
                  </div>
                  <div className="space-y-5">
                    {g.items.map((q) => {
                      const ans = answers[q.id] || {};
                      const val = ans.selected_option ?? ans.text_answer ?? '';
                      const pr = perResultMap[q.id] || null;
                      return (
                        <div key={q.id} data-testid={`ielts-rd-q-${q.question_number}`}>
                          <div className="flex items-start gap-2.5 mb-1.5">
                            <span className="flex-shrink-0 mt-0.5 w-7 h-7 rounded-md bg-black text-white flex items-center justify-center text-xs font-bold">
                              {q.question_number}
                            </span>
                            <p className={`flex-1 text-sm sm:text-[15px] text-gray-900 ${EXAM_TEXT_CLS}`}>
                              {q.question_text}
                            </p>
                          </div>
                          <div className="pl-9.5" style={{ paddingLeft: 38 }}>
                            <QuestionItem
                              q={q}
                              value={val}
                              locked={locked || !!pr}
                              perResult={pr}
                              onChange={onChangeAnswer}
                            />
                            {pr?.explanation && (
                              <div className="mt-2 text-xs text-gray-600 bg-amber-50 border border-amber-200 rounded p-2">
                                <span className="font-semibold text-amber-900">Explanation:</span>{' '}
                                <span className={EXAM_TEXT_CLS}>{pr.explanation}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            {!questions.length && (
              <div className="text-sm text-gray-500 text-center py-10">
                No questions linked to this passage yet.
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default IeltsReadingExam;
